import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  closeMissingEntitiesModal,
  selectMissingEntitiesModal,
  setMissingEntitiesSelection,
} from "../../app/slice/missingEntitiesSlice";
import { IFramePostMessageType, sendPostMessage } from "../../utils/helpers";
import { ConditionFact } from "@reconvert/reconvert-utils";
import SearchProductModal from "../CheckoutPage/components/ConditionCreationSidebar/AddConditionView/SearchProductModal";

/**
 * Global modal opened when the AI chatbot sends a missing-entities message
 * (product_id, product_ids, or collection_id). Renders SearchProductModal and
 * stores the user's selection in Redux, then sends it back to the chatbot.
 */
const MissingEntitiesModal = () => {
  const dispatch = useAppDispatch();
  const { isOpen, missingEntities, selection } = useAppSelector(selectMissingEntitiesModal);

  const needsProduct =
    missingEntities.includes("product_id") || missingEntities.includes("product_ids");
  const needsCollection = missingEntities.includes("collection_id");

  // Prefer product selection when both are requested
  const isProductMode = needsProduct || !needsCollection;

  const modalValue: Record<string, string[]> = isProductMode
    ? selection.productSelection ?? {}
    : (selection.collectionSelection ?? []).reduce<Record<string, string[]>>(
        (acc, item) => {
          acc[item.id] = acc[item.id] ?? [];
          acc[item.id].push(item.name);
          return acc;
        },
        {}
      );

  const handleSetValue = useCallback(
    (data: Record<string, string[]>, options?: { productTitles?: Record<string, string> }) => {
      if (isProductMode) {
        const productTitles = options?.productTitles ?? {};

        dispatch(setMissingEntitiesSelection({ productSelection: data }));
        sendPostMessage(IFramePostMessageType.MISSING_ENTITIES_RESPONSE, {
          selection: {
            productSelection: Object.entries(data).map(([productId, variantIds]) => ({
              productId,
              productName: productTitles[productId] ?? "",
              showAllVariants: variantIds.length === 0,
              selectedVariantIds: variantIds,
            })),
          },
        });
      } else {
        const collectionSelection = Object.entries(data).map(([id, names]) => ({
          id,
          name: names?.[0] ?? "",
        }));

        dispatch(setMissingEntitiesSelection({ collectionSelection }));
        sendPostMessage(IFramePostMessageType.MISSING_ENTITIES_RESPONSE, {
          selection: {
            collectionSelection:
              collectionSelection.length > 0
                ? {
                    collectionId: collectionSelection[0].id,
                    collectionName: collectionSelection[0].name,
                  }
                : undefined,
          },
        });
      }

      dispatch(closeMissingEntitiesModal());
    },
    [dispatch, isProductMode]
  );

  const handleClose = useCallback(() => {
    dispatch(closeMissingEntitiesModal());
  }, [dispatch]);

  if (!isOpen || (!needsProduct && !needsCollection)) {
    return null;
  }

  return (
    <SearchProductModal
      value={modalValue}
      setValue={handleSetValue}
      handleOnClose={handleClose}
      conditionFact={isProductMode ? ConditionFact.SPECIFIC_PRODUCT : ConditionFact.PRODUCT_COLLECTION}
    />
  );
};

export default MissingEntitiesModal;
